import { useState } from 'react';
import { Trans } from '@lingui/react/macro';

import Badge from '@/design_system/Badge';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconChevron from '@/icons/Chevron.svg';
import { Workflow } from '@/models/workflow';
import { useDatabaseTranslation } from '@/services/i18n';

const TitleAndDescription = () => (
  <Stack>
    <Title as="h2">
      <Trans id="settings.workflows.general.request-custom-fields.title">
        Request custom fields
      </Trans>
    </Title>
    <Paragraph size="200" color="secondary">
      <Trans id="settings.workflows.general.request-custom-fields.description">
        Additional and custom information asked at request creation.
      </Trans>
    </Paragraph>
  </Stack>
);

export const RequestCustomFields = ({ workflow }: { workflow: Workflow }) => {
  const { _db } = useDatabaseTranslation();
  const [showCustomFields, setShowCustomFields] = useState(false);

  const customFields = workflow.config.fields?.request?.information?.customFields;

  // It was a product decision to hide this feature to organizations that don't have custom fields
  if (!customFields?.length) {
    return null;
  }

  const customFieldsLength = customFields.length;

  return (
    <Box gap="1rem">
      <TitleAndDescription />

      <div>
        <Button
          variant="tertiary"
          size="small"
          onPress={() => setShowCustomFields(!showCustomFields)}
        >
          <IconChevron down={!showCustomFields} up={showCustomFields} />
          {showCustomFields ? (
            <Trans id="settings.workflows.general.request-custom-fields.hide-custom-fields">
              Hide {customFieldsLength} custom fields
            </Trans>
          ) : (
            <Trans id="settings.workflows.general.request-custom-fields.show-custom-fields">
              Show {customFieldsLength} custom fields
            </Trans>
          )}
        </Button>
      </div>

      {showCustomFields && (
        <Stack gap="0.5rem">
          {customFields.map((customField) => (
            <Box key={customField.id}>
              <Stack row justifyContent="space-between">
                {_db(customField.label)}

                <Stack row gap="0.5rem">
                  {customField.enabled ? (
                    <Badge size="small" color="primary" hasBorder>
                      <Trans id="settings.workflows.general.request-custom-fields.details.enabled">
                        Enabled
                      </Trans>
                    </Badge>
                  ) : (
                    <Badge size="small" color="warning" hasBorder>
                      <Trans id="settings.workflows.general.request-custom-fields.details.disabled">
                        Disabled
                      </Trans>
                    </Badge>
                  )}
                  {customField.required ? (
                    <Badge size="small" color="warning" hasBorder>
                      <Trans id="settings.workflows.general.request-custom-fields.details.required">
                        Required
                      </Trans>
                    </Badge>
                  ) : (
                    <Badge size="small" color="neutral" hasBorder>
                      <Trans id="settings.workflows.general.request-custom-fields.details.optional">
                        Optional
                      </Trans>
                    </Badge>
                  )}
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};
